import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useTeamKnowledgeBase } from "@/hooks/useTeamKnowledgeBase";
import { supabase } from "@/integrations/supabase/client";
import { ConfluenceLayout } from "@/components/knowledge/ConfluenceLayout";
import { ConfluenceNavSidebar } from "@/components/knowledge/ConfluenceNavSidebar";
import { ConfluenceHome } from "@/components/knowledge/ConfluenceHome";
import { ConfluencePageView } from "@/components/knowledge/ConfluencePageView";
import { ConfluenceInlineEditor } from "@/components/knowledge/ConfluenceInlineEditor";
import { Loader2, BookOpen } from "lucide-react";
import { useLocation } from "react-router-dom";
import type { TeamKnowledgeBaseEntry, KnowledgeBaseAttachment } from "@/types/team";

const Archive = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [teamId, setTeamId] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  
  // Handle deep-linking from notifications
  useEffect(() => {
    const state = location.state as { selectedArticleId?: string } | null;
    if (state?.selectedArticleId) {
      setSelectedEntryId(state.selectedArticleId);
      // Clear the state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  const [editingEntry, setEditingEntry] = useState<TeamKnowledgeBaseEntry | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creatorNames, setCreatorNames] = useState<Record<string, string>>({});
  const [creatorAvatars, setCreatorAvatars] = useState<Record<string, string>>({});
  const [userName, setUserName] = useState<string>("");
  const [userAvatar, setUserAvatar] = useState<string>("");

  // Fetch user's team and permissions
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        setProfileLoading(false);
        return;
      }

      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('team_id, first_name, last_name, avatar_url')
          .eq('user_id', user.id)
          .single();

        if (error) throw error;

        if (profile && profile.team_id) {
          setTeamId(profile.team_id);
          setUserName([profile.first_name, profile.last_name].filter(Boolean).join(' '));
          setUserAvatar(profile.avatar_url || '');

          // Check if user is team leader or moderator
          const { data: team } = await supabase
            .from('teams')
            .select('team_leader_id')
            .eq('id', profile.team_id)
            .single();

          const isTeamLeader = team?.team_leader_id === user.id;

          // Check if user is a moderator
          const { data: moderatorRole } = await supabase
            .from('team_member_roles')
            .select('id')
            .eq('team_id', profile.team_id)
            .eq('user_id', user.id)
            .eq('role', 'moderator')
            .maybeSingle();

          const isModerator = !!moderatorRole;

          setCanEdit(isTeamLeader || isModerator);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  const {
    entries,
    categories,
    loading: entriesLoading,
    createEntry,
    updateEntry,
    deleteEntry,
    togglePin
  } = useTeamKnowledgeBase(teamId || undefined);

  // Filter entries based on search
  const filteredEntries = searchQuery
    ? entries.filter(e => 
        e.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (e.category && e.category.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : entries;

  // Fetch creator names and avatars
  useEffect(() => {
    const fetchCreatorInfo = async () => {
      if (entries.length === 0) return;

      const creatorIds = [...new Set(entries.map(e => e.created_by))];
      const { data } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name, avatar_url')
        .in('user_id', creatorIds);

      if (data) {
        const names: Record<string, string> = {};
        const avatars: Record<string, string> = {};
        data.forEach(p => {
          names[p.user_id] = [p.first_name, p.last_name].filter(Boolean).join(' ') || 'Unbekannt';
          avatars[p.user_id] = p.avatar_url || '';
        });
        setCreatorNames(names);
        setCreatorAvatars(avatars);
      }
    };

    fetchCreatorInfo();
  }, [entries]);

  const selectedEntry = selectedEntryId
    ? entries.find(e => e.id === selectedEntryId) || null
    : null;

  const handleCreateNew = () => {
    setEditingEntry(null);
    setShowEditor(true);
  };

  const handleEdit = (entry: TeamKnowledgeBaseEntry) => {
    setEditingEntry(entry);
    setShowEditor(true);
  };

  const handleSave = async (data: {
    title: string;
    content: string;
    category?: string;
    is_pinned?: boolean;
    attachments?: KnowledgeBaseAttachment[];
  }, entryId?: string) => {
    setSaving(true);
    try {
      // Use entryId if passed (inline editing), otherwise check editingEntry (modal editing)
      const idToUpdate = entryId || editingEntry?.id;
      
      if (idToUpdate) {
        const success = await updateEntry(idToUpdate, data);
        return success;
      } else {
        const result = await createEntry(data);
        if (result) {
          setSelectedEntryId(result.id);
        }
        return !!result;
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId: string) => {
    await deleteEntry(entryId);
    setSelectedEntryId(null);
  };

  const handleTogglePin = async (entryId: string, currentPinned: boolean) => {
    await togglePin(entryId, currentPinned);
  };

  const handleEntrySelect = (entryId: string) => {
    setSelectedEntryId(entryId);
    setSelectedCategory(null);
  };

  const handleCategorySelect = (category: string | null) => {
    setSelectedCategory(category);
    setSelectedEntryId(null);
  };

  const handleBack = () => {
    setSelectedEntryId(null);
  };

  const loading = profileLoading || entriesLoading;

  // No team assigned
  if (!loading && !teamId) {
    return (
      <div className="min-h-screen flex w-full bg-[#FAFBFC] dark:bg-background">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-full bg-[#DEEBFF] dark:bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <BookOpen className="h-10 w-10 text-[#0052CC] dark:text-primary" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Kein Team zugewiesen</h2>
            <p className="text-muted-foreground">
              Du musst einem Team angehören, um auf die Wissensbank zugreifen zu können.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex w-full bg-[#FAFBFC] dark:bg-background items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#0052CC] dark:text-primary" />
          <p className="text-sm text-muted-foreground">Lade Wissensbank...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfluenceLayout
        sidebar={
          <ConfluenceNavSidebar
            entries={filteredEntries}
            categories={categories}
            selectedCategory={selectedCategory}
            selectedEntryId={selectedEntryId}
            onCategorySelect={handleCategorySelect}
            onEntrySelect={handleEntrySelect}
            onCreateNew={handleCreateNew}
            canEdit={canEdit}
          />
        }
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        userName={userName}
        userAvatar={userAvatar}
        entries={entries}
        onEntrySelect={handleEntrySelect}
      >
        {selectedEntry ? (
          <ConfluencePageView
            entry={selectedEntry}
            onEdit={() => handleEdit(selectedEntry)}
            onDelete={() => handleDelete(selectedEntry.id)}
            onTogglePin={() => handleTogglePin(selectedEntry.id, selectedEntry.is_pinned)}
            onBack={handleBack}
            onSave={handleSave}
            canEdit={canEdit}
            creatorName={creatorNames[selectedEntry.created_by]}
            creatorAvatar={creatorAvatars[selectedEntry.created_by]}
          />
        ) : (
          <ConfluenceHome
            entries={filteredEntries}
            selectedCategory={selectedCategory}
            onEntrySelect={handleEntrySelect}
            onCreateNew={handleCreateNew}
            canEdit={canEdit}
          />
        )}
      </ConfluenceLayout>

      {/* Editor Modal */}
      <ConfluenceInlineEditor
        open={showEditor}
        onClose={() => {
          setShowEditor(false);
          setEditingEntry(null);
        }}
        onSave={handleSave}
        entry={editingEntry}
        existingCategories={categories}
        saving={saving}
      />
    </>
  );
};

export default Archive;
